/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependdencies
import { useEffect, useMemo, useRef } from 'react';

// UI lib components
import Table, { useTable } from '@ui-kit/organisms/Table';
import { Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';

// Store
import useWorkspace from '@features/Workspace/store/hooks/useWorkspace';
import useAccount from '@features/Account/store/useAccount';

// Helpers
import { getResourceFilterFromScope } from '@helpers/api';
import baseConfig from './base-config';
import useDatadumpFolderIds from '@features/Resource/hooks/useDatadumpFolderIds';

// API & types
import {
  ResourceIndexView,
  SortDirection,
  useResourcesIndexViewQuery,
} from '@app-types/blackroom-api';

// Types
import { ResourceListProps } from './types';
import useColumns from './useColumns';

// Types
import { MRT_TableOptions } from 'mantine-react-table';

// Style
import '@features/Resource/ResourceTable/styles/index.css';
import omit from 'lodash/omit';
import { vars } from '@app/shared/themes/vars';
import { transformToNestedData } from './utils/transformToNestedData';

// Context menu
import { useContextMenu } from 'mantine-contextmenu';
import ResourceContextMenu from '../ResourceContextMenu';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

function ResourceList(listProps: ResourceListProps) {
  /* ********************************** PROPS ********************************* */

  const {
    columns,
    scope = 'my',
    fetchVariables,
    rowVirtualizerInstanceRef,
    onDataChange,
    mantineTableBodyRowProps: externalMantineTableBodyRowProps,
    ...props
  } = listProps;
  /* ********************************** HOOKS ********************************* */

  // Store
  const workspace = useWorkspace();
  const { isCurrentWorkspaceAdmin } = useAccount();
  const { t } = useTranslation();
  const { showContextMenu } = useContextMenu();

  // Ref
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // API
  const { dataroomFolderId, datadumpFolderId } = useDatadumpFolderIds();

  const { data, loading: isFetching } = useResourcesIndexViewQuery({
    variables: {
      ...{
        filter: {
          workspaceId: workspace.id,
          ...fetchVariables?.filter,
          ...getResourceFilterFromScope(scope),
        },
        sorter: {
          index: SortDirection.Asc,
        },
      },
      ...(omit(fetchVariables, 'filter') ?? {}),
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  // Transform the data once when it changes
  const nestedData = useMemo(() => {
    const transformedData = transformToNestedData(
      (data?.resourcesIndexView ?? []) as ResourceIndexView[],
    );

    // Notify parent of data changes
    onDataChange?.((data?.resourcesIndexView ?? []) as ResourceIndexView[]);
    return transformedData;
  }, [data]);

  // Table
  const columnDefs = useColumns({ columns, scope, listProps });
  const listConfig: MRT_TableOptions<ResourceIndexView> = {
    ...baseConfig,
    columns: columnDefs,
    data: nestedData,
    mantineTableContainerProps: {
      ref: tableContainerRef,
      style: {
        scrollbarColor: `${vars.colors.blackroomBlack} ${vars.colors.blackroomWhite}`,
        scrollbarWidth: 'thin',
        height: '100%',
      },
    },
    mantineTableBodyRowProps: (params) => {
      const externalProps =
        typeof externalMantineTableBodyRowProps === 'function'
          ? externalMantineTableBodyRowProps(params)
          : (externalMantineTableBodyRowProps ?? {});

      return {
        ...externalProps,
        onContextMenu: showContextMenu(
          (close) => (
            <ResourceContextMenu
              resources={[params.row.original]}
              allowedActions={['rename', 'archive']}
              onClose={() => close()}
              renderWithoutTarget
            />
          ),
          {
            styles: {
              root: {
                borderRadius: vars.radius.md,
                padding: '0.25rem',
              },
            },
          },
        ),
        style: {
          cursor: 'pointer',
          ...((externalProps as any)?.style ?? {}),
        },
      };
    },
    enableTopToolbar: false,
    displayColumnDefOptions: {
      'mrt-row-select': {
        visibleInShowHideMenu: false,
      },
      'mrt-row-expand': {
        header: undefined,
      },
    },
    meta: {
      context: { scope, dataroomFolderId, datadumpFolderId },
    },
    getRowId: (originalRow) => originalRow.id,
    enablePagination: false,
    enableBottomToolbar: false,
    enableRowVirtualization: true,
    enableFullScreenToggle: false,
    renderEmptyRowsFallback: () => (
      <Stack align="center" justify="center" py="xl" gap="xs">
        <Text c="dimmed" fz="sm">
          {!isCurrentWorkspaceAdmin && scope === 'my'
            ? t('Resource:PageResources.noData.noPermissionsMessage')
            : t('Resource:PageResources.noData.message')}
        </Text>
      </Stack>
    ),
    rowVirtualizerInstanceRef,
    rowVirtualizerOptions: { overscan: 15 },
    ...props,
    state: {
      isLoading: isFetching,
      columnOrder: [
        'mrt-row-expand',
        ...columnDefs.map((col) => col.accessorKey as string),
      ],
      ...props.state,
    },
  };

  const table = useTable(listConfig);

  /* ***************************** LOCAL VARIABLES **************************** */

  /* ******************************** CALLBACKS ******************************* */

  /* ****************************** SIDE-EFFECTS ****************************** */

  useEffect(() => {
    if (rowVirtualizerInstanceRef.current) {
      try {
        rowVirtualizerInstanceRef.current.scrollToIndex(0);
      } catch (e) {
        console.error(e);
      }
    }
  }, []);

  /* ******************************** RENDERING ******************************* */

  return <Table table={table} unstyledShell />;
}

export default ResourceList;
